import { DecimalPipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { isRangeString } from '../../functions/is-range-string.function';
import { rangeFromString } from '../../functions/range-from-string.function';
import { IntRangeInterface } from '../../models/int-range/int-range.interface';
import { IntRange } from '../../models/int-range/int-range.model';
import { PlainRangeInterface } from '../../models/range.interface';

@Pipe({
  name: 'ttNumberRange',
  standalone: true,
})
export class TtNumberRangePipe<
  PlainType extends string | number = number,
  D extends
    | PlainRangeInterface<StartBoundary, EndBoundary>
    | IntRange<StartBoundary, EndBoundary>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | IntRangeInterface<StartBoundary, EndBoundary> = any,
  StartBoundary extends 'finite' | 'infinite' = D['start'] extends null
    ? 'infinite'
    : D['start'] extends PlainType
      ? 'finite'
      : 'infinite',
  EndBoundary extends 'finite' | 'infinite' = D['end'] extends null
    ? 'infinite'
    : D['end'] extends PlainType
      ? 'finite'
      : 'infinite',
> implements PipeTransform {
  public transform(
    value: null | undefined,
    options: {
      format?: string;
      divider?: string;
      shorten?: boolean;
      locale?: string;
    },
  ): null;
  public transform(
    value: D | number | string,
    options: {
      format?: string;
      divider?: string;
      shorten?: boolean;
      locale?: string;
    },
  ): string;

  // eslint-disable-next-line max-lines-per-function
  public transform(
    value: D | string | number | null | undefined,
    options: {
      format?: string;
      divider?: string;
      shorten?: boolean;
      locale?: string;
      infiniteNotation?: string;
    },
  ): string | null {
    options = { divider: '-', infiniteNotation: '∞', shorten: true, ...options };
    const pipe = new DecimalPipe(options.locale ?? 'nl-NL');
    const formatString: string = options.format ?? '1.0-3';

    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      if (isRangeString(value)) {
        value = rangeFromString<StartBoundary, EndBoundary>(value) as D;
      } else {
        value = Number(value);
      }
    }

    if (typeof value === 'number') {
      return pipe.transform(value, formatString, options.locale);
    }

    const range = IntRange.fromObject(value);
    const startString =
      range.min !== null
        ? (pipe.transform(range.min, formatString, options.locale) ?? '')
        : (options.infiniteNotation as string);
    const endString =
      range.max !== null
        ? (pipe.transform(range.max, formatString, options.locale) ?? '')
        : (options.infiniteNotation as string);

    // check if format doesn't contain any smaller than a day
    if (options.shorten && startString === endString) {
      return startString;
    }

    return `${startString}${options.divider ?? ''}${endString}`;
  }
}
